import makeStyles from '@mui/styles/makeStyles'
import { getSufficientContrast } from '../../theme/palette/helpers'

export const clamp = lines => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
})

/* One static rule per tone in `palette.updateType`, so components compose
 * `clsx(classes.root, classes[tone])` and every instance shares one sheet. */
const toneRules = palette =>
  Object.fromEntries(
    Object.entries(palette.updateType).map(([tone, { main, tint }]) => [
      tone,
      { backgroundColor: tint, color: main }
    ])
  )

/* ---- type / plan chips: text colour on a tint, tokens from palette.updateType */
export const useChipStyles = makeStyles(({ palette, typography }) => ({
  chip: {
    height: 24,
    fontWeight: typography.weight.semiBold,
    fontSize: '0.75rem',
    '& .MuiChip-label': {
      paddingLeft: 10,
      paddingRight: 10
    }
  },
  ...toneRules(palette)
}))

/* ---- "New" pill: same accessible brand pair the primary ActionButton uses */
export const useNewChipStyles = makeStyles(({ palette, typography, radius }) => {
  const { main, contrast } = getSufficientContrast(palette, palette.white.main, palette.primary.main)
  return {
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      padding: '0 8px',
      borderRadius: radius.full,
      backgroundColor: contrast,
      color: main,
      fontSize: '0.6875rem',
      fontWeight: typography.weight.bold,
      letterSpacing: '0.6px',
      lineHeight: 1,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    }
  }
})

/* ---- thumbnail: the image when there is one, else a flat tile in the type tint
 * so rows stay aligned (the "neutral placeholder" rule from the brief). */
export const useThumbnailStyles = makeStyles(({ palette, radius }) => ({
  root: {
    flexShrink: 0,
    borderRadius: radius.lg,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  ...toneRules(palette),
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}))

/* ---- 300px card for the home rail */
export const useRailCardStyles = makeStyles(({ palette, spacing, typography, customShadows }) => ({
  card: {
    height: spacing(40),
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: customShadows.cardHover
    }
  },
  action: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start'
  },
  body: {
    padding: spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    flexGrow: 1,
    width: '100%'
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.75),
    flexWrap: 'wrap'
  },
  title: {
    ...typography.heading.h6,
    ...clamp(2),
    color: palette.text.primary
  },
  date: {
    ...typography.body.xs,
    color: palette.grey.shade[70],
    marginTop: 'auto'
  }
}))
